import Icon from "@avinet/adaptive-ui-core/ui/Icon";
import { t } from "i18next";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DEFAULT_API_CONFIG } from "../../api/api-config";
import { OpenIcon } from "../../assets/icons/OpenIcon";
import useDataFetch from "../../hooks/useDataFetch";
import { exportToCSV } from "../../utils/csv-export";
import { IOrder } from "../../utils/data-types";
import { formatDate, formatPrice } from "../../utils/functions";
import { ExportButton } from "../buttons/ExportButton";
import Search from "../search/Search";
import { Text } from "../text/Text";
import "./OrderTable.scss";

interface OrderTableProps {
  customerId: number | undefined;
  setIsOrderlineView: () => void;
  setOrderId: (orderId: number) => void;
}

export const OrderTable = ({
  customerId,
  setIsOrderlineView,
  setOrderId,
}: OrderTableProps) => {
  const [displayedRows, setDisplayedRows] = useState(5);
  const amountOfExtraRowsToShow = 5;

  const [sortOn, setSortOn] = useState<string>("ordre_dato");
  const [sortDirection, setSortDirection] = useState<string>("desc");

  const [filteredData, setFilteredData] = useState<IOrder[]>([]);
  const prevFilteredDataRef = useRef<IOrder[]>([]);

  const [url, setUrl] = useState<string>(
    DEFAULT_API_CONFIG.url + "/crm/ordre/get/" + customerId
  );

  useEffect(() => {
    if (customerId && customerId !== 0) {
      setUrl(DEFAULT_API_CONFIG.url + "/crm/ordre/get/" + customerId);
    }
  }, [customerId]);

  const [data] = useDataFetch<IOrder[]>(url, "GET");

  const dataSortedByDate = useMemo(() => {
    return data?.sort((a, b) => {
      return (
        new Date(b.ordre_dato).getTime() - new Date(a.ordre_dato).getTime()
      );
    });
  }, [data]);

  useEffect(() => {
    if (dataSortedByDate) {
      setFilteredData(dataSortedByDate);
    }
  }, [dataSortedByDate]);

  useEffect(() => {
    prevFilteredDataRef.current = filteredData;
  }, [filteredData]);

  const exportData = useMemo(() => {
    return dataSortedByDate?.map((order) => {
      return {
        Ordrenummer: order.ordreid,
        Dato: formatDate(order.ordre_dato),
        "Deira Ref.": order.deres_ref,
        Merknad: order.eref,
        Total: formatPrice(order.total),
      };
    });
  }, [dataSortedByDate]);

  const handleOrderClick = useCallback(
    (orderId: number) => {
      setOrderId(orderId);
      setIsOrderlineView();
    },
    [setIsOrderlineView, setOrderId]
  );

  const handleExportData = useCallback(() => {
    if (exportData) {
      exportToCSV(exportData, "ngir_" + (data ?? [])[0]?.kundenavn + "_orders");
    }
  }, [data, exportData]);

  const showMoreString = useMemo(() => {
    return t("pages.customersInfo.showMore", {
      amount:
        filteredData?.length === undefined
          ? 0
          : amountOfExtraRowsToShow + displayedRows > filteredData?.length
          ? filteredData?.length
          : amountOfExtraRowsToShow + displayedRows,
    });
  }, [displayedRows, filteredData]);

  const showAllString = t("pages.customersInfo.showAll", {
    amount: filteredData?.length,
  });

  const handleSearch = useCallback((searchResults: IOrder[]) => {
    setFilteredData(searchResults);
  }, []);

  const handleSortData = useCallback(
    (sortKey: keyof IOrder) => {
      const newDirection =
        sortOn === sortKey && sortDirection === "asc" ? "desc" : "asc";
      setSortOn(sortKey);
      setSortDirection(newDirection);

      const sortedData = [...filteredData].sort((a, b) => {
        if (sortKey === "ordre_dato") {
          return newDirection === "asc"
            ? new Date(a.ordre_dato).getTime() -
                new Date(b.ordre_dato).getTime()
            : new Date(b.ordre_dato).getTime() -
                new Date(a.ordre_dato).getTime();
        }
        if (sortKey === "total") {
          return newDirection === "asc" ? a.total - b.total : b.total - a.total;
        }
        if (sortKey === "ordreid") {
          return newDirection === "asc"
            ? a.ordreid - b.ordreid
            : b.ordreid - a.ordreid;
        }
        if (sortKey === "deres_ref") {
          return newDirection === "asc"
            ? a.deres_ref.localeCompare(b.deres_ref)
            : b.deres_ref.localeCompare(a.deres_ref);
        }
        if (sortKey === "eref") {
          return newDirection === "asc"
            ? a.eref.localeCompare(b.eref)
            : b.eref.localeCompare(a.eref);
        }
        return 0;
      });

      setFilteredData(sortedData);
    },
    [filteredData, sortDirection, sortOn]
  );

  return (
    <div className="order-table" id="table">
      <div className="order-table__header">
        <div className="order-table__header__title-n-search">
          <Text
            text={t("pages.customersInfo.order")}
            size="small"
            color="dark"
            fontWeight={600}
          />
          <Search
            placeholder={t("pages.customersInfo.searchOrdersPlaceholder")}
            data={dataSortedByDate}
            onSearch={handleSearch}
          />
        </div>
        <ExportButton onClick={handleExportData} isCsv />
      </div>

      <div className="order-table__content">
        <div className="table-header">
          <div className="table-cell" onClick={() => handleSortData("ordreid")}>
            <Text
              className="sorted"
              text={t("pages.customersInfo.ordernumber")}
              size="xxxs"
            />
            {sortOn === "ordreid" && sortDirection === "asc" && (
              <Icon name="arrow" rotate={270} />
            )}
            {sortOn === "ordreid" && sortDirection === "desc" && (
              <Icon name="arrow" rotate={90} />
            )}
          </div>
          <div
            className="table-cell"
            onClick={() => handleSortData("ordre_dato")}
          >
            <Text
              className="sorted"
              text={t("pages.customersInfo.orderDate")}
              size="xxxs"
            />
            {sortOn === "ordre_dato" && sortDirection === "asc" && (
              <Icon name="arrow" rotate={270} />
            )}
            {sortOn === "ordre_dato" && sortDirection === "desc" && (
              <Icon name="arrow" rotate={90} />
            )}
          </div>
          <div
            className="table-cell"
            onClick={() => handleSortData("deres_ref")}
          >
            <Text
              className="sorted"
              text={t("pages.customersInfo.theirRef")}
              size="xxxs"
            />
            {sortOn === "deres_ref" && sortDirection === "asc" && (
              <Icon name="arrow" rotate={270} />
            )}
            {sortOn === "deres_ref" && sortDirection === "desc" && (
              <Icon name="arrow" rotate={90} />
            )}
          </div>
          <div className="table-cell" onClick={() => handleSortData("eref")}>
            <Text
              className="sorted"
              text={t("pages.customersInfo.eref")}
              size="xxxs"
            />
            {sortOn === "eref" && sortDirection === "asc" && (
              <Icon name="arrow" rotate={270} />
            )}
            {sortOn === "eref" && sortDirection === "desc" && (
              <Icon name="arrow" rotate={90} />
            )}
          </div>
          <div className="table-cell" onClick={() => handleSortData("total")}>
            <Text
              className="sorted"
              text={t("pages.customersInfo.total")}
              size="xxxs"
            />
            {sortOn === "total" && sortDirection === "asc" && (
              <Icon name="arrow" rotate={270} />
            )}
            {sortOn === "total" && sortDirection === "desc" && (
              <Icon name="arrow" rotate={90} />
            )}
          </div>
          <div className="table-cell"></div> {/* Empty cell for alignment */}
        </div>
        <div className="order-table__rows">
          {filteredData &&
            filteredData
              .slice(0, displayedRows)
              .map((row, rowIndex: number) => (
                <div className="table-row" key={rowIndex}>
                  <div className="table-cell">
                    <Text
                      text={filteredData[rowIndex].ordreid.toString()}
                      size="xxs"
                      fontWeight={600}
                    />
                  </div>
                  <div className="table-cell">
                    <Text
                      text={formatDate(filteredData[rowIndex].ordre_dato)}
                      size="xxs"
                    />
                  </div>
                  <div className="table-cell">
                    <Text text={filteredData[rowIndex].deres_ref} size="xxs" />
                  </div>
                  <div className="table-cell">
                    <Text text={filteredData[rowIndex].eref} size="xxs" />
                  </div>
                  <div className="table-cell">
                    <Text
                      text={formatPrice(filteredData[rowIndex].total)}
                      size="xxs"
                    />
                  </div>
                  <div className="table-cell">
                    <div
                      className="btn-container"
                      title={t("pages.customersInfo.showOrder") ?? ""}
                    >
                      <OpenIcon
                        onClick={() =>
                          handleOrderClick(filteredData[rowIndex].ordreid)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
          {filteredData && displayedRows < filteredData.length && (
            <div className="table-row show-more-container">
              <div
                className="show-more"
                onClick={() =>
                  setDisplayedRows(displayedRows + amountOfExtraRowsToShow)
                }
              >
                <Text text={showMoreString} size="xxs" fontWeight={600} />
                <Icon name="add" />
              </div>
              <div
                className="show-all"
                onClick={() => setDisplayedRows(filteredData.length)}
              >
                <Text text={showAllString} size="xxs" fontWeight={600} />
                <Icon name="add" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
